import { NavLink, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  IoCubeOutline,
  IoCarOutline,
  IoFlashOutline,
  IoLocateOutline,
  IoLayersOutline,
  IoSyncOutline,
  IoSettingsOutline,
  IoLogOutOutline,
  IoGridOutline,
  IoCashOutline,
  IoReturnDownBackOutline,
  IoMegaphoneOutline,
  IoLockClosedOutline,
  IoSparklesOutline,
  IoChatbubbleEllipsesOutline,
} from 'react-icons/io5';
import { AuthProvider, useAuth } from './auth';
import { canAccessSection, homeRoute, ROLE_LABELS, type Role } from './permissions';
import { DashboardPage } from './pages/DashboardPage';
import { FinancePage } from './pages/FinancePage';
import { ReturnsPage } from './pages/ReturnsPage';
import { ActionsPage } from './pages/ActionsPage';
import { ProductsPage } from './pages/ProductsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { StrategiesPage } from './pages/StrategiesPage';
import { StrategyEditPage } from './pages/StrategyEditPage';
import { StrategyProductsPage } from './pages/StrategyProductsPage';
import { OzonStrategiesPage } from './pages/OzonStrategiesPage';
import { RepricersPage } from './pages/RepricersPage';
import { ShipmentsPage } from './pages/ShipmentsPage';
import { ShipmentDetailPage } from './pages/ShipmentDetailPage';
import { PickingListPage } from './pages/PickingListPage';
import { SyncPage } from './pages/SyncPage';
import { SettingsPage } from './pages/SettingsPage';
import { AiInsightsPage } from './pages/AiInsightsPage';
import { ChatPage } from './pages/ChatPage';
import { LoginPage } from './pages/LoginPage';

export function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

function AppShell() {
  const { user, loading } = useAuth();
  const loc = useLocation();

  if (loading) return <div style={{ padding: 32 }}>Загрузка…</div>;

  if (!user) {
    if (loc.pathname !== '/login') return <Navigate to="/login" replace />;
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const role = user.role as Role;

  if (loc.pathname === '/login') return <Navigate to={homeRoute(role)} replace />;

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="content">
        <Routes>
          <Route path="/" element={<Navigate to={homeRoute(role)} replace />} />
          <Route path="/dashboard" element={<Guard section="dashboard"><DashboardPage /></Guard>} />
          <Route path="/finance" element={<Guard section="finance"><FinancePage /></Guard>} />
          <Route path="/returns" element={<Guard section="returns"><ReturnsPage /></Guard>} />
          <Route path="/actions" element={<Guard section="actions"><ActionsPage /></Guard>} />
          <Route path="/products" element={<Guard section="products"><ProductsPage /></Guard>} />
          <Route path="/products/:productId" element={<Guard section="products"><ProductDetailPage /></Guard>} />
          <Route path="/strategies" element={<Guard section="strategies"><StrategiesPage /></Guard>} />
          <Route path="/strategies/new" element={<Guard section="strategies"><StrategyEditPage /></Guard>} />
          <Route path="/strategies/:id" element={<Guard section="strategies"><StrategyEditPage /></Guard>} />
          <Route path="/strategies/:id/products" element={<Guard section="strategies"><StrategyProductsPage /></Guard>} />
          <Route path="/ozon-strategies" element={<Guard section="ozon_strategies"><OzonStrategiesPage /></Guard>} />
          <Route path="/repricers" element={<Guard section="repricers"><RepricersPage /></Guard>} />
          <Route path="/shipments" element={<Guard section="shipments"><ShipmentsPage /></Guard>} />
          <Route path="/shipments/picking-list" element={<Guard section="shipments"><PickingListPage /></Guard>} />
          <Route path="/shipments/:postingNumber" element={<Guard section="shipments"><ShipmentDetailPage /></Guard>} />
          <Route path="/sync" element={<Guard section="sync"><SyncPage /></Guard>} />
          <Route path="/ai" element={<Guard section="ai_insights"><AiInsightsPage /></Guard>} />
          <Route path="/chat" element={<Guard section="ai_chat"><ChatPage /></Guard>} />
          <Route path="/settings" element={<Guard section="settings"><SettingsPage /></Guard>} />
        </Routes>
      </main>
    </div>
  );
}

function Guard({ section, children }: { section: string; children: ReactNode }) {
  const { user } = useAuth();
  if (!canAccessSection(user?.role as Role, section)) {
    return <ForbiddenScreen />;
  }
  return <>{children}</>;
}

function ForbiddenScreen() {
  const { user } = useAuth();
  const role = user?.role as Role | undefined;
  return (
    <div className="card" style={{ padding: 40, textAlign: 'center', maxWidth: 480, margin: '60px auto' }}>
      <IoLockClosedOutline style={{ fontSize: 48, color: 'var(--text-muted)' }} />
      <h3 style={{ marginTop: 16 }}>Нет доступа</h3>
      <div className="muted" style={{ marginTop: 8 }}>
        Эта страница недоступна для роли «{role ? ROLE_LABELS[role] : 'неизвестно'}». Обратитесь к администратору если нужен доступ.
      </div>
      <NavLink to={homeRoute(role)} className="btn primary" style={{ marginTop: 16, display: 'inline-flex' }}>
        На главную
      </NavLink>
    </div>
  );
}

// Список пунктов меню. Фильтруется по правам.
const NAV_ITEMS: { to: string; section: string; label: string; icon: ReactNode }[] = [
  { to: '/dashboard',       section: 'dashboard',       label: 'Дашборд',           icon: <IoGridOutline /> },
  { to: '/finance',         section: 'finance',         label: 'Финансы',           icon: <IoCashOutline /> },
  { to: '/products',        section: 'products',        label: 'Товары',            icon: <IoCubeOutline /> },
  { to: '/shipments',       section: 'shipments',       label: 'Отгрузки',          icon: <IoCarOutline /> },
  { to: '/returns',         section: 'returns',         label: 'Возвраты',          icon: <IoReturnDownBackOutline /> },
  { to: '/actions',         section: 'actions',         label: 'Акции',             icon: <IoMegaphoneOutline /> },
  { to: '/repricers',       section: 'repricers',       label: 'Репрайсеры',        icon: <IoFlashOutline /> },
  { to: '/strategies',      section: 'strategies',      label: 'Стратегии (наши)',  icon: <IoLocateOutline /> },
  { to: '/ozon-strategies', section: 'ozon_strategies', label: 'Стратегии Ozon',    icon: <IoLayersOutline /> },
  { to: '/ai',              section: 'ai_insights',     label: 'AI-аналитика',      icon: <IoSparklesOutline /> },
  { to: '/chat',            section: 'ai_chat',         label: 'AI чат',            icon: <IoChatbubbleEllipsesOutline /> },
  { to: '/sync',            section: 'sync',            label: 'Синхронизация',     icon: <IoSyncOutline /> },
  { to: '/settings',        section: 'settings',        label: 'Настройки',         icon: <IoSettingsOutline /> },
];

function Sidebar() {
  const { user, logout } = useAuth();
  const role = user?.role as Role | undefined;
  const items = NAV_ITEMS.filter((i) => canAccessSection(role, i.section));

  return (
    <aside className="sidebar">
      <h1>Ozon koleso tools</h1>
      <nav>
        {items.map((i) => (
          <NavLink key={i.to} to={i.to} className={({ isActive }) => (isActive ? 'active' : '')}>
            <span className="icon">{i.icon}</span> {i.label}
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-footer">
        <div style={{ color: '#cbd5e1', marginBottom: 10 }}>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{user?.email}</div>
          <div style={{ fontSize: 11, color: '#8a8f99' }}>
            {role ? ROLE_LABELS[role] : 'неизвестно'}
          </div>
        </div>
        <button
          onClick={() => logout()}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)',
            color: '#cbd5e1', padding: '7px 12px', borderRadius: 6, cursor: 'pointer',
            width: '100%', fontSize: 12, justifyContent: 'center', fontFamily: 'inherit',
          }}
        >
          <IoLogOutOutline /> Выйти
        </button>
      </div>
    </aside>
  );
}
